/**
 * 720 全景引擎组件
 * 负责创建和管理第三方 720° 全景引擎实例
 * 镜像 Engine2d 组件的设计模式
 */
import type { ThemeConfig } from '../../themes/types';
import { IBimComponent } from '../../types/component';
import { themeManager } from '../../services/theme';
import type { Engine720Options, PanoramaLoadOptions, PanoramaAnnotation } from './types';
import type { ManagerRegistry } from '../../core/manager-registry';
// 导入第三方 SDK 的 createEngine720 函数（从 npm 包引入）
import { createEngine720 as createEngine720SDK } from 'iflow-engine-base';
import "../../../../bim_engine_base/dist/iflow-engine-base.css";

export type { Engine720Options, PanoramaLoadOptions, PanoramaAnnotation };

/**
 * 创建 Engine720 实例的工厂函数
 * 兼容旧代码直接 import 的方式
 */
export const createEngine720 = (options: Engine720Options, registry: ManagerRegistry) => {
    return new Engine720(options, registry);
};

/**
 * 720° 全景引擎组件
 * 负责创建和管理第三方 720 全景引擎实例
 */
export class Engine720 implements IBimComponent {
    /** 第三方 720 引擎实例 */
    private engine: any = null;
    /** 管理器注册表实例 */
    private registry: ManagerRegistry;
    /** 引擎挂载的容器元素 */
    private container: HTMLElement;
    /** 引擎容器 ID（用于传递给 createEngine720） */
    private containerId: string;
    /** 引擎配置选项（不包含 container） */
    private options: Omit<Engine720Options, 'container'>;
    /** 是否已初始化 */
    private _isInitialized = false;
    /** 是否已销毁 */
    private _isDestroyed = false;
    /** 主题订阅取消函数 */
    private unsubscribeTheme: (() => void) | null = null;

    /**
     * 构造函数
     * @param options 720 引擎配置选项
     * @param registry 管理器注册表
     */
    constructor(options: Engine720Options, registry: ManagerRegistry) {
        // 保存注册表
        this.registry = registry;
        // 解析容器元素
        this.container = options.container;
        // 如果容器没有 id，生成一个唯一的 id
        if (!this.container.id) {
            this.containerId = `engine720-container-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            this.container.id = this.containerId;
        } else {
            this.containerId = this.container.id;
        }

        // 保存配置选项（设置默认值）
        this.options = {
            fov: options.fov ?? 75,
            enableZoom: options.enableZoom ?? true,
            enableRotate: options.enableRotate ?? true,
            sphereRadius: options.sphereRadius ?? 500,
            rotateSpeed: options.rotateSpeed,
            zoomSpeed: options.zoomSpeed,
            enableDamping: options.enableDamping,
            dampingFactor: options.dampingFactor,
            minFov: options.minFov,
            maxFov: options.maxFov,
        };
    }

    /**
     * 初始化组件 (接口实现)
     * 创建 div 容器并初始化 720 引擎
     */
    public init(): void {
        if (this._isInitialized) {
            console.warn('[Engine720] Engine already initialized.');
            return;
        }

        if (this._isDestroyed) {
            console.error('[Engine720] Cannot initialize destroyed engine.');
            return;
        }

        try {
            // 创建引擎配置对象
            const engineConfig: any = {
                containerId: this.containerId,
                fov: this.options.fov,
                enableZoom: this.options.enableZoom,
                enableRotate: this.options.enableRotate,
                sphereRadius: this.options.sphereRadius,
            };

            // 仅在用户提供时传递可选参数
            if (this.options.rotateSpeed !== undefined) {
                engineConfig.rotateSpeed = this.options.rotateSpeed;
            }
            if (this.options.zoomSpeed !== undefined) {
                engineConfig.zoomSpeed = this.options.zoomSpeed;
            }
            if (this.options.enableDamping !== undefined) {
                engineConfig.enableDamping = this.options.enableDamping;
            }
            if (this.options.dampingFactor !== undefined) {
                engineConfig.dampingFactor = this.options.dampingFactor;
            }
            if (this.options.minFov !== undefined) {
                engineConfig.minFov = this.options.minFov;
            }
            if (this.options.maxFov !== undefined) {
                engineConfig.maxFov = this.options.maxFov;
            }

            // 输出配置信息
            console.log('[Engine720] 引擎配置信息：', engineConfig);

            // 调用引擎创建函数创建 720 引擎实例
            this.engine = createEngine720SDK(engineConfig);

            if (!this.engine) {
                throw new Error('Failed to create 720 engine instance');
            }

            // 标记为已初始化
            this._isInitialized = true;

            // 订阅主题变化
            this.unsubscribeTheme = themeManager.subscribe((theme) => {
                this.setTheme(theme);
            });

            // 应用当前主题
            this.setTheme(themeManager.getTheme());

            // 监听 720 全景事件
            if (this.engine.events) {
                this.engine.events.on('panorama-loaded', (data: any) => {
                    console.log('[Engine720] 全景加载完成:', data);
                    this.registry.emit('engine720:panorama-loaded', { data });
                });

                this.engine.events.on('panorama-load-error', (data: any) => {
                    console.error('[Engine720] 全景加载失败:', data);
                    this.registry.emit('engine720:load-error', { data });
                });

                this.engine.events.on('annotation-click', (data: any) => {
                    console.log('[Engine720] 标注点击:', data);
                    this.registry.emit('engine720:annotation-click', { data });
                });

                this.engine.events.on('view-angle-changed', (data: any) => {
                    this.registry.emit('engine720:view-changed', { data });
                });
            }

        } catch (error) {
            console.error('[Engine720] Failed to initialize 720 engine:', error);
            this._isInitialized = false;
            throw error;
        }
    }

    /**
     * 设置主题 (接口实现)
     * @param _theme 全局主题配置
     */
    public setTheme(_theme: ThemeConfig): void {
        // 720 引擎暂时不需要主题切换（全景图本身覆盖整个视口）
        if (!this._isInitialized || !this.engine) return;
        // 预留：如果底层引擎支持 setTheme，可在此调用
    }

    /**
     * 设置语言 (接口实现)
     */
    public setLocales(): void {
        // 720 引擎组件暂时不需要本地化
    }

    /**
     * 检查是否已初始化
     */
    public isInitialized(): boolean {
        return this._isInitialized;
    }

    /**
     * 加载全景图
     * @param url 全景图片 URL
     * @param _options 加载选项（预留）
     */
    public async loadPanorama(url: string, _options?: PanoramaLoadOptions): Promise<void> {
        if (!this._isInitialized || !this.engine) {
            console.error('[Engine720] Engine not initialized. Please call init() first.');
            return;
        }
        if (!url) {
            console.error('[Engine720] Panorama URL is required.');
            return;
        }

        try {
            console.log('[Engine720] 开始加载全景图:', url);
            await this.engine.loadPanorama(url);
            console.log('[Engine720] 全景图加载完成:', url);
        } catch (error) {
            console.error('[Engine720] 全景图加载失败:', error);
            throw error;
        }
    }

    /**
     * 预加载多个全景图
     * @param urls 全景图 URL 列表
     */
    public async preloadPanoramas(urls: string[]): Promise<void> {
        if (!this._isInitialized || !this.engine) return;
        try {
            await this.engine.preloadPanoramas?.(urls);
        } catch (e) {
            console.warn('[Engine720] Failed to preload panoramas:', e);
        }
    }

    /**
     * 设置视场角
     * @param fov 视场角值
     */
    public setFov(fov: number): void {
        if (!this._isInitialized || !this.engine) return;
        this.engine.setFov?.(fov);
    }

    /**
     * 获取当前视场角
     * @returns 当前视场角值
     */
    public getFov(): number {
        if (!this._isInitialized || !this.engine) return 75;
        return this.engine.getFov?.() ?? 75;
    }

    /**
     * 设置相机朝向
     * @param phi 水平角度
     * @param theta 垂直角度
     * @param animated 是否动画过渡
     */
    public lookAt(phi: number, theta: number, animated?: boolean): void {
        if (!this._isInitialized || !this.engine) return;
        this.engine.lookAt?.(phi, theta, animated);
    }

    /**
     * 重置视图
     */
    public resetView(): void {
        if (!this._isInitialized || !this.engine) return;
        try {
            // 重置到默认视角
            this.engine.lookAt?.(0, 0, true);
            this.engine.setFov?.(75);
        } catch (e) {
            console.warn('[Engine720] Failed to reset view:', e);
        }
    }

    /**
     * 暂停渲染
     */
    public pauseRendering(): void {
        if (!this._isInitialized || !this.engine) return;
        this.engine.pauseRendering?.();
    }

    /**
     * 恢复渲染
     */
    public resumeRendering(): void {
        if (!this._isInitialized || !this.engine) return;
        this.engine.resumeRendering?.();
    }

    /**
     * 调整引擎尺寸
     * @param width 宽度
     * @param height 高度
     */
    public resize(width?: number, height?: number): void {
        if (!this._isInitialized || !this.engine) return;
        this.engine.resize?.(width, height);
    }

    /**
     * 订阅原始 720 引擎事件
     * @param event 事件名称
     * @param handler 事件处理函数
     */
    public onRawEvent(event: string, handler: (...args: any[]) => void): void {
        this.engine?.events?.on(event, handler);
    }

    /**
     * 取消订阅原始 720 引擎事件
     * @param event 事件名称
     * @param handler 事件处理函数
     */
    public offRawEvent(event: string, handler: (...args: any[]) => void): void {
        this.engine?.events?.off(event, handler);
    }

    /**
     * 销毁组件 (接口实现)
     * 清理资源、取消订阅、销毁引擎实例
     */
    public destroy(): void {
        if (this._isDestroyed) {
            return;
        }

        // 取消主题订阅
        if (this.unsubscribeTheme) {
            this.unsubscribeTheme();
            this.unsubscribeTheme = null;
        }

        // 销毁 720 引擎，释放资源
        if (this.engine) {
            try {
                this.engine.dispose?.();
            } catch (e) {
                console.warn('[Engine720] Error during dispose:', e);
            }
            this.engine = null;
        }

        // 清理容器
        this.container.innerHTML = '';

        // 更新状态
        this._isDestroyed = true;
        this._isInitialized = false;
    }
}
